import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { Split, X, Plus, Trash2, Info } from "lucide-react";

const ratioLabel = (r) => {
    if (r >= 1) return `${+r.toFixed(r % 1 ? 2 : 0)}-for-1 split`;
    const inv = 1 / r;
    return `1-for-${+inv.toFixed(inv % 1 ? 2 : 0)} reverse split`;
};

export function SplitsNotice() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ticker: "", date: "", ratio: "" });
    const { data } = useQuery({
        queryKey: ["splits"],
        queryFn: async () => (await api.get("/corporate-actions/splits")).data,
    });

    const detected = data?.detected || [];
    const manual = data?.manual || [];
    const unseen = detected.filter((d) => !d.acknowledged);

    const refresh = () => qc.invalidateQueries();

    const ack = async (d) => {
        try {
            await api.post("/corporate-actions/splits/ack", { ticker: d.ticker, date: d.date });
            refresh();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const dismissAll = async () => {
        try {
            await Promise.all(unseen.map((d) => api.post("/corporate-actions/splits/ack", { ticker: d.ticker, date: d.date })));
            refresh();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const addManual = async () => {
        try {
            await api.post("/corporate-actions/splits/manual", {
                ticker: form.ticker, date: form.date, ratio: parseFloat(form.ratio),
            });
            toast.success("Split added. Share counts updated.");
            setForm({ ticker: "", date: "", ratio: "" });
            setOpen(false);
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const delManual = async (id) => {
        try {
            await api.delete(`/corporate-actions/splits/manual/${id}`);
            qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const manageDialog = (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="manage-splits-btn">
                    <Plus className="w-3 h-3" /> Add / manage splits
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Stock splits</DialogTitle>
                    <DialogDescription>
                        Splits are auto-detected from market data and applied to your share counts. Add one manually if a split is missing or for an untracked symbol.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-1">
                        <label className="text-xs text-muted-foreground">Ticker</label>
                        <Input data-testid="split-ticker-input" value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })} placeholder="AAPL" />
                    </div>
                    <div className="col-span-1">
                        <label className="text-xs text-muted-foreground">Date</label>
                        <Input data-testid="split-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </div>
                    <div className="col-span-1">
                        <label className="text-xs text-muted-foreground">Ratio (new/old)</label>
                        <Input data-testid="split-ratio-input" type="number" step="0.01" value={form.ratio} onChange={(e) => setForm({ ...form, ratio: e.target.value })} placeholder="2 = 2:1" />
                    </div>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" /> Use 2 for a 2-for-1 split, or 0.1 for a 1-for-10 reverse split. Trades before this date are scaled automatically.</p>
                {manual.length > 0 && (
                    <div className="border-t border-border pt-3">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Your manual splits</div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {manual.map((m) => (
                                <div key={m.id} className="flex items-center justify-between text-sm" data-testid={`manual-split-${m.id}`}>
                                    <span className="num"><b>{m.ticker}</b> · {fmtDate(m.date)} · {ratioLabel(m.ratio)}</span>
                                    <button onClick={() => delManual(m.id)} className="text-muted-foreground hover:text-loss" data-testid={`del-split-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button onClick={addManual} data-testid="save-split-btn" disabled={!form.ticker || !form.date || !form.ratio}>Add split</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    if (unseen.length === 0) {
        // Keep a quiet entry point to manage splits even when nothing is pending.
        return <div className="flex justify-end">{manageDialog}</div>;
    }

    return (
        <Card className="p-4 border-l-4 border-l-primary bg-primary/5" data-testid="splits-notice">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-primary/15 p-1.5"><Split className="w-4 h-4 text-primary" /></div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h3 className="font-heading text-sm font-semibold">Stock split{unseen.length > 1 ? "s" : ""} detected &amp; applied</h3>
                        <button onClick={dismissAll} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="dismiss-all-splits"><X className="w-3 h-3" /> Dismiss all</button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Your share counts and average costs have been adjusted automatically. Your transaction history is unchanged.</p>
                    <div className="mt-3 space-y-1.5">
                        {unseen.map((d) => (
                            <div key={`${d.ticker}-${d.date}`} className="flex items-center justify-between text-sm" data-testid={`split-notice-${d.ticker}-${d.date}`}>
                                <span><b className="num">{d.ticker}</b> — {ratioLabel(d.ratio)} on {fmtDate(d.date)}{d.source === "manual" ? " (manual)" : ""}</span>
                                <button onClick={() => ack(d)} className="text-xs text-primary hover:underline" data-testid={`ack-split-${d.ticker}-${d.date}`}>Got it</button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3">{manageDialog}</div>
                </div>
            </div>
        </Card>
    );
}
